import './style.less';

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import ProLayout from '@ant-design/pro-layout';
import { Button } from 'antd';
import { sortBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useIsFetching } from 'react-query';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import { NavHashLink } from 'react-router-hash-link';

import PageLoader from '../components/common/PageLoader';
import { useCarriersList, useSelectedCarrier } from '../hooks/apiHooks';
import SidebarFooter from './SidebarFooter';

/*
interface ILayoutProps {
  menuIndex: number;
  routes: IPageRoute[];
  showCarriersNavigation?: boolean;
  disableLoading?: boolean;
}
*/

const Layout = ({
  carrierLoading,
  children,
  disableLoading,
  menuIndex,
  routes,
  showCarriersNavigation,
  skipFullPageLoader,
}) => {
  const carrierMatch = useRouteMatch('/carrier/:carrierId');
  const isFetching = useIsFetching({
    predicate: (query) => {
      const key = query.queryKey[0];
      // don't show loader for these queries
      // the other queries are used to populate the autocomplete dropdowns and doesn't need full page reload
      return !['rateAuditByCarrier', 'makes', 'models', 'vinaudit', 'paths'].includes(key);
    }
  });
  const { hash, pathname } = useLocation();
  const { carriersList } = useCarriersList(showCarriersNavigation);
  const { selectedCarrier } = useSelectedCarrier();
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem('menuCollapsed');
    return stored ? JSON.parse(stored) : false;
  });
  const [openKeys, setOpenKeys] = useState(() => {
    const stored = localStorage.getItem('menuOpenKeys');
    return stored ? JSON.parse(stored) : ['rates', 'paths'];
  });

  // Save openKeys to localStorage
  useEffect(() => {
    localStorage.setItem('menuOpenKeys', JSON.stringify(openKeys));
  }, [openKeys]);

  // Save collapsed state to localStorage and dispatch event to sync tabs
  useEffect(() => {
    localStorage.setItem('menuCollapsed', JSON.stringify(collapsed));
    window.dispatchEvent(new Event('menuCollapsedChange'));
  }, [collapsed]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.type === 'storage' && e.key !== 'menuCollapsed') return;

      const stored = localStorage.getItem('menuCollapsed');
      const newCollapsed = stored ? JSON.parse(stored) : false;
      setCollapsed(newCollapsed);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const visibleRoutes = useMemo(
    () => routes.filter((route) => route.menuIndex === menuIndex),
    [menuIndex, routes]
  );

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const sortedCarriersList = useMemo(
    () => sortBy(carriersList, (carrier) => carrier.name),
    [carriersList]
  );

  const domainLogo = () => {
    if (window.location.host === 'www.carshippingrates.ca') {
      return '/assets/images/logo_csr_white.png';
    } else {
      return '/assets/images/logo_white.png';
    }
  };

  const domainLogoIcon = () => {
    return '/assets/images/logo_icon.png';
  };

  return (
    <ProLayout
      fixSiderbar
      collapsed={collapsed}
      collapsedButtonRender={false}
      headerContentRender={() => null}
      location={{ pathname: pathname + hash }}
      logo={collapsed ? domainLogoIcon() : domainLogo()}
      menuFooterRender={() => (
        <SidebarFooter
          carriers={sortedCarriersList}
          collapsed={collapsed}
          loading={carrierLoading}
          selectedCarrier={selectedCarrier}
          showCarriersNavigation={showCarriersNavigation}
        />
      )}
      menuHeaderRender={(logo, title) => (
        <div className={`logo-container ${collapsed ? 'logo-container-collapsed' : ''}`}>
          <Link className={`logo ${collapsed ? 'logo-collapsed' : ''}`} to="/">
            {logo}
            {!collapsed && title}
          </Link>
          <Button
            className="collapse-toggle-btn"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            size="small"
            onClick={toggleCollapsed}
          />
        </div>
      )}
      menuItemRender={(item, dom) => {
        let path = item.path;
        const carrierId = carrierMatch?.params?.carrierId ?? selectedCarrier?.id?.toString();
        if (carrierId && path?.includes(':carrierId')) {
          path = path.replace(/:carrierId/g, carrierId);
        }
        if (path?.includes('settings#')) {
          return (
            <NavHashLink
              smooth
              scroll={(el) =>
                el.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
              to={path}
            >
              {dom}
            </NavHashLink>
          );
        }
        return <Link to={path || '/'}>{dom}</Link>;
      }}
      menuProps={
        collapsed
          ? {
            _internalDisableMenuItemTitleTooltip: true,
            inlineCollapsed: true,
            triggerSubMenuAction: 'hover',
          }
          : {
            onOpenChange: setOpenKeys,
            openKeys,
          }
      }
      navTheme="light"
      pageTitleRender={() =>
        window.location.host === 'www.carshippingrates.ca'
          ? 'carshippingrates.ca'
          : 'Carrier Portal'
      }
      route={{
        path: '/',
        routes: visibleRoutes,
      }}
      siderWidth={250}
      style={{ height: '100vh' }}
      title=""
      onCollapse={(newCollapsed) => {
        const stored = localStorage.getItem('menuCollapsed');
        const storedCollapsed = stored ? JSON.parse(stored) : collapsed;

        setCollapsed(newCollapsed === storedCollapsed ? newCollapsed : storedCollapsed);
      }}
    >
      {(isFetching && (!disableLoading || !skipFullPageLoader)) || (carrierLoading && !skipFullPageLoader) ? <PageLoader /> : children}
    </ProLayout>
  );
};

export default Layout;
